import {IServiceFactory} from "../service-factory.interface";
import {
    FlightScheduleAvailabilityStatusEnum,
    IFlightScheduleCache,
    IFlightScheduleDayCache
} from "./flights-schedule.service.interface";
import {Check} from "../../types/type-checking";
import {makeObservable, observable, runInAction} from "mobx";
import {TimeSpan} from "../../types/time-span";


export class FlightScheduleRange {
    constructor(private readonly origin: string,
                private readonly destination: string,
                private readonly fromDate: Date,
                private readonly toDate: Date,
                private readonly cache: IFlightScheduleCache,
                private readonly services: IServiceFactory) {

        makeObservable<this, '_isReading'>(this, {
            _isReading: observable.ref,
        });

        this._loadSchedulePromise = this._loadSchedule();
    }

    private _isReading: boolean = true;
    private _loadSchedulePromise: Promise<void>;

    private _startReading(): void {
        runInAction(() => {
            this._isReading = true;
        })
    }

    private _stopReading(): void {
        runInAction(() => {
            this._isReading = false;
        })
    }

    private _saveToCache(formattedDate: string, status: FlightScheduleAvailabilityStatusEnum): void {
        this.cache.putInCache(this.origin, this.destination, formattedDate, status);
    }

    private _getFromCache(formattedDate: string): IFlightScheduleDayCache | null {
        return this.cache.getFromCache(this.origin, this.destination, formattedDate);
    }


    private _forEachDayInRange(callback: (formattedDate: string) => void): void {
        for(let date = this.fromDate; date.getTime() <= this.toDate.getTime(); date = this.services.time.addDays(date, 1)) {
            callback(this._formatDate(date));
        }
    }

    isDateInRange(date: Date): boolean {
        return this.fromDate.getTime() <= date.getTime() && date.getTime() <= this.toDate.getTime();
    }

    getSchedule(date: Date): FlightScheduleAvailabilityStatusEnum {
        if(date < this.services.time.makeShortDate(this.services.time.currentDate)) {
            return FlightScheduleAvailabilityStatusEnum.NotAvailable;
        }

        if(this._isReading) {
            return FlightScheduleAvailabilityStatusEnum.Reading;
        }

        const result = this._getFromCache(this._formatDate(date));

        if(Check.isNullOrUndefined(result) || result.isExpired) {
            this._startReading();
            this._loadSchedulePromise = this._loadSchedule();
            return FlightScheduleAvailabilityStatusEnum.Reading;
        }

        if(Check.isNullOrUndefined(result.dayScheduleStatus)) {
            return FlightScheduleAvailabilityStatusEnum.NotAvailable;
        }

        return result.dayScheduleStatus;
    }

    async getScheduleAsync(date: Date): Promise<FlightScheduleAvailabilityStatusEnum> {
        await this._loadSchedulePromise;
        return this.getSchedule(date);
    }

    private async _loadSchedule(): Promise<void> {

        try {
            this._startReading();

            if(!this.cache.isCacheExpired(this.origin, this.destination, this._formatDate(this.fromDate))) {
                return;
            }

            const session = await this.services.user.getSession();
            const tripScheduleResponse = await session.tripSchedule({
                beginDate: this._formatDate(this.fromDate),
                endDate: `${this._formatDate(this.toDate)}T23:59:59.000Z`,
                origin: this.origin,
                destination: this.destination
            });

            if(tripScheduleResponse.hasErrors) {
                this._setNotAvailableForAll();

                if(!tripScheduleResponse.hasDotRezError('nsk:InvalidMarket')) {
                    this.cache.setExpirationTime(this.origin, this.destination, TimeSpan.fromSeconds(5));
                }

            } else {
                const availableDates = new Set(tripScheduleResponse.data.map(schedule => this._formatDate(schedule.latest)));

                runInAction(() => {
                    this._forEachDayInRange(formattedDate => {
                        this._saveToCache(formattedDate,
                            availableDates.has(formattedDate)
                                ?  FlightScheduleAvailabilityStatusEnum.Available
                                : FlightScheduleAvailabilityStatusEnum.NotAvailable);
                    })

                });
            }

        } catch (err) {
            this.services.logger.error(`Failed to read schedule for ${this.origin} -> ${this.destination} from ${this._formatDate(this.fromDate)} to ${this._formatDate(this.toDate)}`, err);
            this._setNotAvailableForAll();
            this.cache.setExpirationTime(this.origin, this.destination, TimeSpan.fromSeconds(5));
        } finally {
            this._stopReading();
        }

    }

    private _setNotAvailableForAll(): void {
        runInAction(() => {
            this._forEachDayInRange(formattedDate => {
                this._saveToCache(formattedDate,
                    FlightScheduleAvailabilityStatusEnum.NotAvailable)
            });
        });
    }

    private _formatDate(date: Date | string): string {
        return this.services.time.formatYYYY_MM_DD(date);
    }
}